/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Canopy is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Canopy is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Canopy, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Canopy:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Canopy broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of selected options
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Canopy utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Canopy only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Canopy.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Canopy is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Canopy provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// ── Notify-me modal ──────────────────────────────────────────────────────────

(function() {
  // Inject modal HTML once
  var modalHTML = `
    <div id="notify-modal" role="dialog" aria-modal="true" aria-labelledby="notify-modal-heading">
      <div id="notify-modal-inner">
        <button id="notify-modal-close" aria-label="Close">&#215;</button>
        <p id="notify-modal-product"></p>
        <h2 id="notify-modal-heading">Notify Me When Back in Stock</h2>
        <p id="notify-modal-sub">Enter your email and we'll let you know as soon as this item is available again.</p>
        <div id="notify-modal-form">
          <input type="email" id="notify-modal-email" placeholder="Your email address" autocomplete="email">
          <button type="button" id="notify-modal-submit" class="btn btn--primary">NOTIFY ME</button>
        </div>
        <div id="notify-modal-success">
          <p>You're on the list!</p>
          <p>We'll email you when this item is back in stock.</p>
          <button type="button" id="notify-modal-success-close" class="btn btn--primary">CLOSE</button>
        </div>
      </div>
    </div>`;
  document.body.insertAdjacentHTML('beforeend', modalHTML);

  var modal        = document.getElementById('notify-modal');
  var emailInput   = document.getElementById('notify-modal-email');
  var submitBtn    = document.getElementById('notify-modal-submit');
  var closeBtn     = document.getElementById('notify-modal-close');
  var successClose = document.getElementById('notify-modal-success-close');
  var formEl       = document.getElementById('notify-modal-form');
  var successEl    = document.getElementById('notify-modal-success');
  var productLabel = document.getElementById('notify-modal-product');
  var currentProduct = '';
  var currentProductUrl = '';

  function openModal(productName, productUrl) {
    currentProduct = productName;
    currentProductUrl = productUrl;
    productLabel.textContent = productName;
    emailInput.value = '';
    emailInput.style.borderColor = '';
    formEl.style.display = '';
    successEl.style.display = 'none';
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    setTimeout(function() { emailInput.focus(); }, 50);
  }

  function closeModal() {
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  // Open when any notify-restock button is clicked
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.js-notify-restock');
    if (btn) {
      openModal(btn.dataset.product || '', btn.dataset.productUrl || '');
      return;
    }

    // Close on overlay click
    if (e.target === modal) closeModal();

    // Close buttons
    if (e.target === closeBtn || e.target === successClose) closeModal();

    // Submit
    if (e.target === submitBtn) {
      if (!emailInput.value || !/\S+@\S+\.\S+/.test(emailInput.value)) {
        emailInput.style.borderColor = 'red';
        emailInput.focus();
        return;
      }
      var formData = new FormData();
      formData.append('form_type', 'contact');
      formData.append('utf8', '✓');
      formData.append('contact[email]', emailInput.value);
      formData.append('contact[Message]', 'Back in stock request');
      formData.append('contact[Product]', currentProduct);
      formData.append('contact[Product URL]', currentProductUrl);
      fetch('/contact', { method: 'POST', body: formData });
      formEl.style.display = 'none';
      successEl.style.display = 'block';
    }
  });

  // Close on Escape key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') closeModal();
  });
})();

/* ---- Brand-prefix fix for the header search (Juan, 2026-10-04) ----
   Shopify's predictive search matches collections by MEANING as well as spelling, so a
   half-typed "padr" is read as Spanish "padre" and the Collections tab fills with My Father.
   After every render: keep only collections whose title has a word starting with every word
   typed; if that empties the tab, refill it from Shopify's own suggestions, shortest first
   ("padron" before "padron x opus"), keeping only collections that match that suggestion.
   Same rule as cigarstacks.com (cs-header-search.liquid). */
(function () {
  if (!('customElements' in window)) return;
  customElements.whenDefined('predictive-search').then(function () {
    var PS = customElements.get('predictive-search');
    if (!PS || PS.prototype.__afcBrandFix) return;
    PS.prototype.__afcBrandFix = true;
    var norm = function (t) {
      return String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    };
    var fitsWords = function (words, title) {
      var tw = norm(title).split(' ');
      return words.every(function (w) { return tw.some(function (x) { return x.indexOf(w) === 0; }); });
    };
    var origRender = PS.prototype.renderResults;
    PS.prototype.renderResults = function (markup) {
      origRender.call(this, markup);
      try { fixCollections(this); } catch (e) { /* never block the search */ }
    };
    function fixCollections(ps) {
      var panel = ps.querySelector('[id$="-collections"][role="tabpanel"]');
      if (!panel) return;
      var links = Array.prototype.slice.call(panel.querySelectorAll('a.predictive-result'));
      if (!links.length) return;
      var words = norm(ps.getQuery()).split(' ').filter(Boolean);
      if (!words.length) return;
      var titleOf = function (a) { var t = a.querySelector('.predictive-result__title'); return t ? t.textContent : a.textContent; };
      var template = links[0].cloneNode(true);
      var parent = links[0].parentElement;
      links.forEach(function (a) { if (!fitsWords(words, titleOf(a))) a.remove(); });
      if (panel.querySelector('a.predictive-result')) return;
      /* Candidates: Shopify's own suggestions that start with what was typed, shortest first. */
      var seen = {};
      var cands = Array.prototype.slice.call(ps.querySelectorAll('.predictive-result--suggestion'))
        .map(function (a) { return norm(a.textContent); })
        .filter(function (t) { if (!t || seen[t] || t.indexOf(words[0]) !== 0) return false; seen[t] = 1; return true; })
        .sort(function (a, b) { return a.length - b.length; })
        .slice(0, 3);
      if (!cands.length) return;
      var term = ps.getQuery();
      var tryNext = function (i) {
        if (i >= cands.length) return;
        var better = cands[i];
        var bw = better.split(' ');
        fetch('/search/suggest.json?q=' + encodeURIComponent(better) + '&resources[type]=collection&resources[limit]=8')
          .then(function (r) { return r.json(); })
          .then(function (j) {
            if (ps.getQuery() !== term || !panel.isConnected) return;
            var cols = ((j.resources && j.resources.results && j.resources.results.collections) || [])
              .filter(function (c) { return fitsWords(bw, c.title); }).slice(0, 6);
            if (!cols.length) { tryNext(i + 1); return; }
            cols.forEach(function (c) {
              var a = template.cloneNode(true);
              a.href = c.url;
              var t = a.querySelector('.predictive-result__title'); if (t) t.textContent = c.title;
              var img = a.querySelector('img');
              if (img) { if (c.image && c.image.src) { img.src = c.image.src; img.srcset = ''; img.alt = c.title; } else { img.remove(); } }
              parent.appendChild(a);
            });
          })
          .catch(function () { tryNext(i + 1); });
      };
      tryNext(0);
    }
  });
})();

/* ---- Search: sold-out matches + drop look-alike products (Juan, 2026-10-07) ----
   Same rule as cigarstacks.com v42. Shopify fills the product list with IN-STOCK look-alikes
   ("olmec" -> Oliva Serie G Maduro) after the real matches, and hides sold-out exact matches.
   1) every predictive-search request and every /search form/link asks for sold-out products too;
   2) after each render, if any product has a word starting with every typed word, the others go. */
(function () {
  var SHOW = 'options[unavailable_products]';
  /* 1a. predictive-search requests */
  if (window.fetch && !window.fetch.__afcShow) {
    var origFetch = window.fetch.bind(window);
    var wrapped = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var route = (window.theme && theme.routes && theme.routes.predictiveSearch) || '/search/suggest';
        if (typeof input === 'string' && url.indexOf(route) === 0 && url.indexOf('unavailable_products') < 0) {
          input = url + (url.indexOf('?') > -1 ? '&' : '?') + encodeURIComponent('resources[options][unavailable_products]') + '=show';
        }
      } catch (e) {}
      return origFetch(input, init);
    };
    wrapped.__afcShow = true;
    window.fetch = wrapped;
  }
  /* 1b. search forms (header bar, mobile, results page) */
  var addHidden = function () {
    Array.prototype.forEach.call(document.querySelectorAll('form[action$="/search"]'), function (f) {
      if (f.querySelector('input[name="' + SHOW + '"]')) return;
      var i = document.createElement('input'); i.type = 'hidden'; i.name = SHOW; i.value = 'show'; f.appendChild(i);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', addHidden); else addHidden();

  if (!('customElements' in window)) return;
  customElements.whenDefined('predictive-search').then(function () {
    var PS = customElements.get('predictive-search');
    if (!PS || PS.prototype.__afcProductFix) return;
    PS.prototype.__afcProductFix = true;
    var norm = function (t) {
      return String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    };
    var origRender = PS.prototype.renderResults;
    PS.prototype.renderResults = function (markup) {
      origRender.call(this, markup);
      try { fixProducts(this); } catch (e) {}
      try { fixLinks(this); } catch (e) {}
    };
    function fixProducts(ps) {
      var panel = ps.querySelector('[id$="-products"][role="tabpanel"]') || ps.querySelector('[id$="-products"]');
      if (!panel) return;
      var links = Array.prototype.slice.call(panel.querySelectorAll('a.predictive-result:not(.predictive-result--suggestion)'));
      if (!links.length) return;
      var words = norm(ps.getQuery()).split(' ').filter(Boolean);
      if (!words.length) return;
      var fits = function (a) {
        var t = a.querySelector('.predictive-result__title') || a;
        var tw = norm(t.textContent).split(' ');
        return words.every(function (w) { return tw.some(function (x) { return x.indexOf(w) === 0; }); });
      };
      var good = links.filter(fits);
      if (!good.length) return;
      links.forEach(function (a) {
        if (good.indexOf(a) > -1) return;
        var row = a.closest('li') || a;
        if (row !== panel) row.remove();
      });
    }
    function fixLinks(ps) {
      Array.prototype.forEach.call(ps.querySelectorAll('a[href*="/search?"]'), function (a) {
        if (a.href.indexOf('unavailable_products') > -1) return;
        a.href = a.href + '&options%5Bunavailable_products%5D=show';
      });
    }
  });
})();

/* ---- Search: ALWAYS include sold-out cigars, on every path (Juan, 2026-10-08) ----
   "when you search a cigar on either site it should show the cigars the customer is typing,
   even the ones that are out of stock … they can always tap notify on restock."
   Shopify's default (unavailable_products=last) parks sold-out products behind hundreds of loose
   matches, so they vanish. Patch 2 fixed typing + the search button; this covers the rest:
   - any /search page opened WITHOUT the option (sort, filter, paging, Google, bookmarks) reloads with it;
   - any fetch to /search (filters/sort/paging done without a page load) carries it;
   - the dropdown asks for 10 products instead of the theme's 4, so every size fits. */
(function () {
  var KEY = 'options[unavailable_products]';
  var ENC = 'options%5Bunavailable_products%5D';
  try {
    var u = new URL(window.location.href);
    if (u.pathname.replace(/\/$/, '') === '/search' && u.searchParams.get('q') && !u.searchParams.has(KEY)) {
      u.searchParams.set(KEY, 'show');
      window.location.replace(u.toString());
      return;
    }
  } catch (e) {}
  if (window.fetch && !window.fetch.__afcShowAll) {
    var prev = window.fetch.bind(window);
    var w = function (input, init) {
      try {
        if (typeof input === 'string') {
          var path = input.split('?')[0].replace(/^https?:\/\/[^/]+/, '');
          if (/^\/search(\/suggest)?$/.test(path.replace(/\/$/, '')) || path.indexOf('/search/suggest') === 0) {
            if (input.indexOf('unavailable_products') < 0) {
              var isSuggest = path.indexOf('/search/suggest') === 0;
              input += (input.indexOf('?') > -1 ? '&' : '?') + (isSuggest ? encodeURIComponent('resources[options][unavailable_products]') : ENC) + '=show';
            }
            if (path.indexOf('/search/suggest') === 0) {
              input = input.replace(/(resources%5Blimit%5D|resources\[limit\])=\d+/, '$1=10')
                           .replace(/(resources%5Blimit_scope%5D|resources\[limit_scope\])=\w+/, '$1=each');
            }
          }
        }
      } catch (e) {}
      return prev(input, init);
    };
    w.__afcShowAll = true; w.__afcShow = true;
    window.fetch = w;
  }
})();

/* ---- Results page: only what the customer typed (Juan, 2026-10-08: "let the search be for what
   they type in"). Shopify pads /search with loose matches (olmec maduro -> 5 Olmecs, then Oliva
   Serie G / Serie O Maduros). On each results page: if any product's title/brand has a word starting
   with every typed word, hide the products that don't, and hide the page numbers once the real
   matches run out. A page with no match at all (a typo like "olvia") is left alone. ---- */
(function () {
  try {
    var u = new URL(window.location.href);
    if (u.pathname.replace(/\/$/, '') !== '/search') return;
    var q = (u.searchParams.get('q') || '').replace(/["']/g, ' ');
    var norm = function (t) { return String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); };
    var words = norm(q).split(' ').filter(Boolean);
    if (!words.length) return;
    var fits = function (txt) {
      var tw = norm(txt).split(' ');
      return words.every(function (w) { return tw.some(function (x) { return x.indexOf(w) === 0; }); });
    };
    var apply = function () {
      var grid = document.querySelector('.main-products-grid__results ul.grid');
      if (!grid) return;
      var items = Array.prototype.filter.call(grid.children, function (li) { return li.querySelector('product-card, .card--product'); });
      if (!items.length) return;
      var good = items.filter(function (li) { var info = li.querySelector('.card__info') || li; return fits(info.textContent); });
      if (!good.length) return;
      var hidden = 0;
      items.forEach(function (li) { var keep = good.indexOf(li) > -1; li.style.display = keep ? '' : 'none'; if (!keep) hidden++; });
      if (hidden) Array.prototype.forEach.call(document.querySelectorAll('.main-products-grid__results nav, .main-products-grid__results .pagination'), function (n) { n.style.display = 'none'; });
    };
    var start = function () {
      apply();
      var root = document.querySelector('.main-products-grid__results') || document.body;
      new MutationObserver(function () { apply(); }).observe(root, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  } catch (e) {}
})();

/* ---- Dropdown top-up (Juan, 2026-10-08: "yes build it for both sites"). Shopify's quick search
   returns only its top results and can rank look-alikes (Oliva Maduro) above real matches
   (Olmec Claro); once those look-alikes are dropped the list is short. Refill it from the matching
   collections' own products (sold-out included), keeping only products with every typed word. ---- */
(function () {
  if (!('customElements' in window)) return;
  customElements.whenDefined('predictive-search').then(function () {
    var PS = customElements.get('predictive-search');
    if (!PS || PS.prototype.__afcTopUp) return;
    PS.prototype.__afcTopUp = true;
    var LIMIT = 10, cache = {};
    var norm = function (t) { return String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); };
    var origRender = PS.prototype.renderResults;
    PS.prototype.renderResults = function (markup) {
      origRender.call(this, markup);
      try { topUp(this); } catch (e) {}
    };
    function topUp(ps) {
      var panel = ps.querySelector('[id$="-products"][role="tabpanel"]') || ps.querySelector('[id$="-products"]');
      if (!panel) return;
      var rows = Array.prototype.slice.call(panel.querySelectorAll('a.predictive-result:not(.predictive-result--suggestion)'));
      if (!rows.length || rows.length >= LIMIT) return;
      var words = norm(ps.getQuery()).split(' ').filter(Boolean);
      if (!words.length) return;
      var fits = function (t) { var tw = norm(t).split(' '); return words.every(function (w) { return tw.some(function (x) { return x.indexOf(w) === 0; }); }); };
      var handles = [];
      Array.prototype.forEach.call(ps.querySelectorAll('[id$="-collections"] a.predictive-result[href*="/collections/"]'), function (a) {
        var t = a.querySelector('.predictive-result__title');
        if (!fits(t ? t.textContent : a.textContent)) return;
        var h = (a.getAttribute('href').split('/collections/')[1] || '').split(/[?#/]/)[0];
        if (h && handles.indexOf(h) < 0) handles.push(h);
      });
      if (!handles.length) return;
      var term = ps.getQuery();
      var tplRow = rows[0].closest('li') || rows[0];
      var parent = tplRow.parentElement;
      var get = function (h) {
        if (!cache[h]) cache[h] = fetch('/collections/' + h + '/products.json?limit=30').then(function (r) { return r.ok ? r.json() : { products: [] }; }).catch(function () { return { products: [] }; });
        return cache[h];
      };
      Promise.all(handles.slice(0, 4).map(get)).then(function (res) {
        if (ps.getQuery() !== term || !parent.isConnected) return;
        var have = {};
        Array.prototype.forEach.call(panel.querySelectorAll('a.predictive-result[href*="/products/"]'), function (a) { have[(a.getAttribute('href').split('/products/')[1] || '').split(/[?#]/)[0]] = 1; });
        var add = [];
        res.forEach(function (j) { (j.products || []).forEach(function (p) {
          if (have[p.handle] || !fits(p.vendor + ' ' + p.title)) return; have[p.handle] = 1; add.push(p);
        }); });
        var room = LIMIT - panel.querySelectorAll('a.predictive-result:not(.predictive-result--suggestion)').length;
        if (room <= 0 || !add.length) return;
        var avail = function (p) { return p.variants.some(function (v) { return v.available; }) ? 1 : 0; };
        add.sort(function (a, b) { return avail(b) - avail(a); });
        add.slice(0, room).forEach(function (p) {
          var row = tplRow.cloneNode(true);
          var a = row.matches('a') ? row : row.querySelector('a');
          if (a) a.setAttribute('href', '/products/' + p.handle);
          var t = row.querySelector('.predictive-result__title'); if (t) t.textContent = p.title;
          var img = row.querySelector('img');
          var src = p.images && p.images[0] && p.images[0].src;
          if (img) { if (src) { img.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=112'; img.removeAttribute('srcset'); img.alt = p.title; } else img.remove(); }
          var prices = p.variants.map(function (v) { return parseFloat(v.price); }).filter(function (x) { return !isNaN(x); });
          var cur = row.querySelector('.price__current'), from = row.querySelector('.price__from'), was = row.querySelector('.price__was');
          if (cur && prices.length) {
            var min = Math.min.apply(null, prices), parts = min.toFixed(2).split('.');
            cur.innerHTML = '$' + parts[0] + '<sup>' + parts[1] + '</sup>';
            if (from) from.style.display = prices.some(function (x) { return x !== min; }) ? '' : 'none';
            if (was) was.textContent = '';
          }
          var viewAll = parent.querySelector(':scope > .js-submit');
          if (viewAll) parent.insertBefore(row, viewAll); else parent.appendChild(row);
        });
      });
    }
  });
})();
